/**
 * Gift Message Custom Element
 * Handles gift message functionality with toggle and form management
 */
class GiftMessage extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.debounceTimeout = 500;

    this.checkbox = null;
    this.form = null;
    this.toInput = null;
    this.fromInput = null;
    this.noteTextarea = null;
    this.debouncedUpdate = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.loadExistingData();
  }

  initializeElements() {
    this.checkbox = this.querySelector(`#gift-message-toggle-${this.sectionId}`);
    this.form = this.querySelector(`#gift-message-form-${this.sectionId}`);
    this.toInput = this.querySelector(`#gift-message-to-${this.sectionId}`);
    this.fromInput = this.querySelector(`#gift-message-from-${this.sectionId}`);
    this.noteTextarea = this.querySelector(`#gift-message-note-${this.sectionId}`);

    if (!this.checkbox || !this.form || !this.toInput || !this.fromInput || !this.noteTextarea) {
      console.error('GiftMessage: Required elements not found');
      return;
    }

    this.debouncedUpdate = FoxTheme.utils.debounce(this.handleFormUpdate.bind(this), this.debounceTimeout);
  }

  setupEventListeners() {
    if (!this.checkbox || !this.debouncedUpdate) return;

    this.checkbox.addEventListener('change', this.handleToggleChange.bind(this));

    if (this.toInput) this.toInput.addEventListener('input', this.debouncedUpdate);
    if (this.fromInput) this.fromInput.addEventListener('input', this.debouncedUpdate);
    if (this.noteTextarea) this.noteTextarea.addEventListener('input', this.debouncedUpdate);
  }

  loadExistingData() {
    if (this.hasExistingData()) {
      this.checkbox.checked = true;
      this.showForm();
    }
  }

  hasExistingData() {
    return (
      (this.toInput && this.toInput.value.trim() !== '') ||
      (this.fromInput && this.fromInput.value.trim() !== '') ||
      (this.noteTextarea && this.noteTextarea.value.trim() !== '')
    );
  }

  handleToggleChange(event) {
    if (event.target.checked) {
      this.showForm();
    } else {
      this.hideForm();
      this.clearFormData();
    }
  }

  showForm() {
    if (this.form) {
      this.form.style.display = '';
      this.form.setAttribute('aria-hidden', 'false');
    }
  }

  hideForm() {
    if (this.form) {
      this.form.style.display = 'none';
      this.form.setAttribute('aria-hidden', 'true');
    }
  }

  clearFormData() {
    if (this.toInput) {
      this.toInput.value = '';
      this.toInput.classList.remove('field-error');
    }
    if (this.fromInput) {
      this.fromInput.value = '';
      this.fromInput.classList.remove('field-error');
    }
    if (this.noteTextarea) {
      this.noteTextarea.value = '';
      this.noteTextarea.classList.remove('field-error');
    }

    this.updateCartAttributes({
      'to': '',
      'from': '',
      'note': ''
    });
  }

  areAllFieldsFilled() {
    return (
      this.toInput && this.toInput.value.trim() !== '' &&
      this.fromInput && this.fromInput.value.trim() !== '' &&
      this.noteTextarea && this.noteTextarea.value.trim() !== ''
    );
  }

  /**
   * Validate all required fields using HTML5 validation
   */
  validateRequiredFields() {
    if (!this.form) return true;

    const requiredFields = [this.toInput, this.fromInput, this.noteTextarea];
    let isValid = true;

    requiredFields.forEach(field => {
      if (field && !field.checkValidity()) {
        isValid = false;
        // Add visual feedback for invalid fields
        field.classList.add('field-error');
      } else if (field) {
        // Remove error styling for valid fields
        field.classList.remove('field-error');
      }
    });

    return isValid;
  }

  /**
   * Collect form data
   */
  collectFormData() {
    return {
      'to': this.toInput ? this.toInput.value.trim() : '',
      'from': this.fromInput ? this.fromInput.value.trim() : '',
      'note': this.noteTextarea ? this.noteTextarea.value.trim() : ''
    };
  }

  handleFormUpdate() {
    if (!this.checkbox.checked) return;

    // Use HTML5 validation to check if all required fields are valid
    if (this.validateRequiredFields() && this.areAllFieldsFilled()) {
      const formData = this.collectFormData();
      this.updateCartAttributes(formData);
    }
  }

  updateCartAttributes(data) {
    const requestBody = JSON.stringify({
      note: `to: ${data.to}, from: ${data.from}, message: ${data.note}`,
    });

    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error updating gift message:', error);
    });
  }
}

customElements.define('gift-message', GiftMessage);
